import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { toast } from "react-toastify";

import { API_bulk_archive } from "../../../../constants";
import { postRightworkAPI } from "../../requests/utilities/requests";
import { summarizeFailureReasons, logBulkFailures } from "./bulkResults";


export default function BulkArchiveModal({
  open,
  onClose,
  selectedUserIds,
  companyId,
  companyStores,
  onSuccess,
  initialScope,
  initialStoreIds,
}) {
  const [scope, setScope] = React.useState("all");
  const [storeIds, setStoreIds] = React.useState([]);
  const [archiveReason, setArchiveReason] = React.useState("");
  const [rehireEligible, setRehireEligible] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // initialScope/initialStoreIds let the drawer's per-row menu open us
      // pre-scoped to one store; selection-based callers omit them and we
      // default to "all of their memberships in this company".
      setScope(initialScope || "all");
      setStoreIds(initialStoreIds || []);
      setArchiveReason("");
      setRehireEligible(true);
    }
  }, [open, initialScope, initialStoreIds]);

  const userCount = selectedUserIds?.size || 0;
  const canSubmit =
    userCount > 0 &&
    !submitting &&
    (scope === "all" || storeIds.length > 0);

  const toggleStore = (id) => {
    setStoreIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        company_id: companyId,
        user_ids: Array.from(selectedUserIds).join(","),
        scope,
        archive_reason: archiveReason.trim(),
        rehire_eligible: rehireEligible,
      };
      if (scope === "stores") body.store_ids = storeIds.join(",");

      const response = await postRightworkAPI(API_bulk_archive, body);
      const payload = response?.data?.redux?.payload || {};
      const succeeded = payload.succeeded_count || 0;
      const failed = payload.failed_count || 0;
      const deactivated = (payload.deactivated_user_ids || []).length;

      if (failed === 0) {
        const cascadeNote = deactivated > 0
          ? ` ${deactivated} user account${deactivated === 1 ? "" : "s"} fully deactivated.`
          : "";
        toast.success(
          `Archived ${succeeded} membership${succeeded === 1 ? "" : "s"}.${cascadeNote}`,
          { autoClose: 4000 },
        );
      } else {
        const reasons = summarizeFailureReasons(payload.results);
        logBulkFailures("bulk_archive", payload.results);
        toast.warning(
          `Archived ${succeeded} of ${succeeded + failed}; ${failed} failed.${reasons ? ` Reasons: ${reasons}` : ""}`,
          { autoClose: 8000 },
        );
      }
      // Pass payload up so the parent can prune deactivated_user_ids from
      // its selection set — those rows vanish from the roster on refetch
      // and would otherwise leave a stale-id ghost in the toolbar count.
      onSuccess?.(payload);
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Bulk archive failed.",
        { autoClose: 4000 },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxHeight: "90vh",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Archive memberships</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <WarningAmberIcon style={{ fontSize: 18 }} className="text-amber-600 mt-0.5" />
            <p className="text-xs text-amber-800">
              Archiving removes the user from the selected stores' schedules and chats.
              Users whose <span className="font-semibold">last membership</span> is archived
              will have their account deactivated and lose all access.
            </p>
          </div>

          <p className="text-sm text-slate-600">
            Archiving <span className="font-semibold">{userCount}</span> selected
            {userCount === 1 ? " user" : " users"}.
          </p>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              Scope
            </p>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="archive-scope"
                  value="all"
                  checked={scope === "all"}
                  onChange={() => setScope("all")}
                  className="accent-violet-700"
                />
                <span className="text-sm text-slate-700">All of their memberships in this company</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="archive-scope"
                  value="stores"
                  checked={scope === "stores"}
                  onChange={() => setScope("stores")}
                  className="accent-violet-700"
                />
                <span className="text-sm text-slate-700">Specific stores only</span>
              </label>
            </div>

            {scope === "stores" && (
              <div className="mt-2 max-h-[180px] overflow-y-auto rounded-lg border border-slate-200">
                {companyStores.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-slate-500">No stores in this company.</div>
                )}
                {companyStores.map((s) => {
                  const checked = storeIds.includes(s.id);
                  const isClosed = s.status === "closed";
                  return (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50"
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => toggleStore(s.id)}
                        size="small"
                        sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, py: 0.5 }}
                      />
                      <StoreOutlinedIcon style={{ fontSize: 16 }} className="text-slate-400" />
                      <span className={`text-sm ${checked ? "text-violet-800 font-medium" : "text-slate-700"}`}>
                        {s.store_name}
                      </span>
                      {isClosed && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded bg-slate-100 text-slate-500">
                          closed
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              Reason (optional)
            </p>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="e.g. Seasonal end, resigned, terminated"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: "8px",
                  fontSize: 14,
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
                },
              }}
            />
          </div>

          <FormControlLabel
            control={
              <Checkbox
                checked={rehireEligible}
                onChange={(e) => setRehireEligible(e.target.checked)}
                sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" } }}
              />
            }
            label={<span className="text-sm text-slate-700">Eligible for rehire</span>}
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <LoadingButton
            variant="contained"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#dc2626",
              "&:hover": { backgroundColor: "#b91c1c" },
              fontSize: 14,
              px: 2.5,
              py: 1,
            }}
          >
            Archive {userCount} {userCount === 1 ? "user" : "users"}
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}
